<template>
    <div class="p-6 w-full max-w-4xl mx-auto">
        <div class="mb-6">
            <h1 class="text-3xl font-bold text-rose-800 mb-2">Contul meu</h1>
            <p class="text-gray-600">Gestionează informațiile contului tău</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Profile Card -->
            <div class="bg-white rounded-lg shadow p-6">
                <div class="flex items-center gap-4 mb-6">
                    <div
                        class="w-16 h-16 bg-gradient-to-br from-rose-700 to-rose-900 rounded-full flex items-center justify-center">
                        <i class="pi pi-user text-white text-3xl"></i>
                    </div>
                    <div>
                        <h2 class="text-xl font-bold text-gray-800">{{ doctorProfile.name }}</h2>
                        <p class="text-gray-600">{{ doctorProfile.speciality }}</p>
                    </div>
                </div>

                <div class="space-y-4">
                    <div>
                        <label class="text-sm text-gray-600">Email</label>
                        <p class="font-medium">{{ doctorProfile.email }}</p>
                    </div>

                    <div>
                        <label class="text-sm text-gray-600">Membru din</label>
                        <p class="font-medium">{{ formatDate(doctorProfile.createdAt) }}</p>
                    </div>
                </div>
            </div>

            <!-- Edit Profile -->
            <div class="bg-white rounded-lg shadow p-6">
                <h2 class="text-xl font-semibold mb-4">Editează profilul</h2>

                <form @submit.prevent="updateProfile" class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Nume complet
                        </label>
                        <InputText data-cy="name-input" v-model="editData.name" class="w-full"
                            placeholder="Dr. Ion Popescu" />
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Specialitate
                        </label>
                        <InputText data-cy="speciality-input" v-model="editData.speciality" class="w-full"
                            placeholder="Cardiolog" />
                    </div>

                    <div v-if="updateSuccess"
                        class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                        <div class="flex items-center gap-2">
                            <i class="pi pi-check-circle"></i>
                            <span>Profil actualizat cu succes!</span>
                        </div>
                    </div>

                    <div v-if="updateError" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        <div class="flex items-center gap-2">
                            <i class="pi pi-exclamation-circle"></i>
                            <span>{{ updateError }}</span>
                        </div>
                    </div>

                    <Button data-cy="save-button" type="submit" label="Salvează modificări" icon="pi pi-save"
                        :loading="updating" class="w-full" severity="success" />
                </form>
            </div>

            <!-- Statistics -->
            <div class="bg-gradient-to-br from-rose-700 to-rose-900 rounded-lg shadow p-6 text-white">
                <h2 class="text-xl font-semibold mb-4">Statistici</h2>

                <div class="space-y-4">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <i class="pi pi-users text-3xl"></i>
                            <div>
                                <p class="text-rose-100 text-sm">Total pacienți</p>
                                <p class="text-2xl font-bold">{{ stats.totalPatients }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <i class="pi pi-file text-3xl"></i>
                            <div>
                                <p class="text-rose-100 text-sm">Total consultații</p>
                                <p class="text-2xl font-bold">{{ stats.totalRecords }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <i class="pi pi-calendar text-3xl"></i>
                            <div>
                                <p class="text-rose-100 text-sm">Luna aceasta</p>
                                <p class="text-2xl font-bold">{{ stats.thisMonth }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Logout -->
            <div class="bg-white rounded-lg shadow p-6">
                <h2 class="text-xl font-semibold mb-4">Acțiuni cont</h2>

                <div class="space-y-3">
                    <Button data-cy="logout-button" label="Deconectare" icon="pi pi-sign-out" @click="handleLogout"
                        severity="danger" outlined class="w-full" />

                    <p class="text-sm text-gray-600 text-center">
                        Vei fi deconectat din aplicație
                    </p>
                </div>
            </div>
            <ConfirmModal v-model:open="confirmOpen" :title="confirmOptions.title" :message="confirmOptions.message"
                :confirmText="confirmOptions.confirmText" :cancelText="confirmOptions.cancelText"
                :confirmVariant="confirmOptions.confirmVariant" :confirmDataCy="confirmOptions.confirmDataCy"
                :cancelDataCy="confirmOptions.cancelDataCy" @confirm="confirmOnConfirm" @cancel="confirmOnCancel" />
        </div>
    </div>
</template>

<script setup>
import { watch, ref } from 'vue'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import { signOut } from 'firebase/auth'
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore'
import { auth, db } from '../../firebase/config.js'
import ConfirmModal from '../ui/ConfirmModal.vue';
import { useConfirm } from '../../composables/useConfirm.js';

const { open: confirmOpen, options: confirmOptions, confirm, onConfirm: confirmOnConfirm, onCancel: confirmOnCancel } = useConfirm();

const props = defineProps({
    user: {
        type: Object,
        default: null
    }
})

const emit = defineEmits(['logout'])

const doctorProfile = ref({
    name: '',
    speciality: '',
    email: '',
    createdAt: ''
})

const editData = ref({
    name: '',
    speciality: ''
})

const stats = ref({
    totalPatients: 0,
    totalRecords: 0,
    thisMonth: 0
})

const updating = ref(false)
const updateSuccess = ref(false)
const updateError = ref('')

const loadDoctorProfile = async () => {
    try {
        const u = props.user || auth.currentUser;
        if (!u) return;

        const docRef = doc(db, 'users', u.uid)
        const docSnap = await getDoc(docRef)

        const defaults = {
            email: u.email || '',
            name: u.email.split('@')[0] || '',
            speciality: 'Doctor',
            role: 'doctor',
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        if (docSnap.exists()) {
            const data = docSnap.data();

            doctorProfile.value = { ...defaults, ...data };

            if (!editData.value.name)
                editData.value.name = doctorProfile.value.name || '';
            if (!editData.value.speciality)
                editData.value.speciality = doctorProfile.value.speciality || '';
        } else {
            doctorProfile.value = defaults;
            await setDoc(docRef, doctorProfile.value);
            editData.value.name = doctorProfile.value.name;
            editData.value.speciality = doctorProfile.value.speciality;
        }
    } catch (error) {
        console.error('Error loading profile:', error)
    }
}

const loadStats = async () => {
    try {
        // Get total patients from 'patients' collection
        const patientsRef = collection(db, 'patients')
        const patientsSnapshot = await getDocs(patientsRef)

        // Get consultations from 'exams' collection
        const examsRef = collection(db, 'exams')
        const querySnapshot = await getDocs(examsRef)

        let thisMonthCount = 0
        const currentMonth = new Date().getMonth()
        const currentYear = new Date().getFullYear()

        querySnapshot.forEach((doc) => {
            const data = doc.data()

            // Count this month's records
            if (data.updatedAt) {
                const recordDate = new Date(data.updatedAt)
                if (recordDate.getMonth() === currentMonth && recordDate.getFullYear() === currentYear) {
                    thisMonthCount++
                }
            }
        })

        stats.value = {
            totalPatients: patientsSnapshot.size,
            totalRecords: querySnapshot.size,
            thisMonth: thisMonthCount
        }
    } catch (error) {
        console.error('Error loading stats:', error)
    }
}

const updateProfile = async () => {
    updating.value = true
    updateSuccess.value = false
    updateError.value = ''

    try {
        const u = props.user || auth.currentUser
        if (!u?.uid) {
            updateError.value = 'Nu există utilizator autentificat.'
            return
        }
        const docRef = doc(db, 'users', u.uid)
        await setDoc(docRef, {
            ...doctorProfile.value,
            name: editData.value.name,
            speciality: editData.value.speciality,
            updatedAt: new Date().toISOString()
        }, { merge: true })

        doctorProfile.value.name = editData.value.name
        doctorProfile.value.speciality = editData.value.speciality

        updateSuccess.value = true
        setTimeout(() => {
            updateSuccess.value = false
        }, 3000)
    } catch (error) {
        console.error('Error updating profile:', error)
        updateError.value = 'Eroare la actualizare: ' + error.message
    } finally {
        updating.value = false
    }
}

const handleLogout = async () => {
    const ok = await confirm({
        title: "Deconectare",
        message: "Ești sigur că vrei să te deconectezi?",
        confirmText: "Deconectare",
        cancelText: "Anulează",
        confirmVariant: "danger",
    });

    if (!ok) return;

    try {
        await signOut(auth);
        emit("logout");
    } catch (error) {
        console.error("Logout error:", error);
        updateError.value = "Eroare la deconectare";
    }
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

watch(
    () => props.user || auth.currentUser,
    (user) => {
        if (user) {
            loadDoctorProfile();
            loadStats();
        }
    },
    { immediate: true }
);
</script>
